// Downloader.js
class Downloader extends EventTarget {
  constructor({
    exportPath = "/api/export",
    statusPath = (jobId) => `/api/status/${jobId}`,
    downloadPath = (jobId) => `/api/download/${jobId}`,
    pollInterval = 800,
    maxWait = 10_000,
    backoff = 1.4,
    maxInterval = 5000,
  } = {}) {
    super();
    this._opts = { exportPath, statusPath, downloadPath, pollInterval, maxWait, backoff, maxInterval };

    this.jobId = null;
    this._status = null;
    this._polling = false;
    this._ready = false;
    this._error = null;
    this._aborted = false;
  }

  // --- getters ---
  get status()    { return this._status; }   // last response from /api/status
  get isReady()   { return this._ready; }    // true if zip is ready
  get isPolling() { return this._polling; }  // polling state, true if active
  get error()     { return this._error; }    // returns error if one occurred

  // --- start uploader ---
  async start({ state, imageFile, svgContent }) {
    try {
      const st = { ...state };

      const form = new FormData();
      form.append("json", JSON.stringify(st));
      if (imageFile) form.append("image", imageFile);
      if (svgContent) form.append("svgContent", svgContent);

      const res = await fetch(this._opts.exportPath, { method: "POST", body: form });
      if (!res.ok) throw new Error(`Export failed: ${res.status}`);
      const { jobId } = await res.json();
      if (!jobId) throw new Error("No jobId returned.");

      this.jobId = jobId;
      this.dispatchEvent(new CustomEvent("started", { detail: { jobId } }));
      return jobId;
    } catch (err) {
      this._error = err;
      this.dispatchEvent(new CustomEvent("error", { detail: err }));
      throw err;
    }
  }

  // --- waiting for results ---
  async waitReady() {
    if (!this.jobId) throw new Error("Job not started.");
    if (this._polling) return;

    this._polling = true;
    this._aborted = false;
    const { statusPath, pollInterval, maxWait, backoff, maxInterval } = this._opts;
    const t0 = Date.now();

    try {
      while (true) {
        if (this._aborted) throw new Error("Polling aborted.");

        const r = await fetch(statusPath(this.jobId), { cache: "no-store" });
        const j = await r.json();
        this._status = j;
        this.dispatchEvent(new CustomEvent("status", { detail: j }));

        if (j.ready) {
          this._ready = true;
          this._polling = false;
          this.dispatchEvent(new CustomEvent("ready", { detail: { jobId: this.jobId } }));
          return true;
        }

        if (Date.now() - t0 > maxWait) {
          const err = new Error("Generation timeout.", {cause: {type: "timeout"}});
          this._error = err;
          this._polling = false;
          this.dispatchEvent(new CustomEvent("timeout", { detail: err }));
          throw err;
        }

        await new Promise(res => setTimeout(res, pollInterval)); //delay
      }
    } catch (err) {
      this._error = err;
      this._polling = false;
      if(err.cause.type != "timeout"){
        this.dispatchEvent(new CustomEvent("error", { detail: err }));
      }
      throw err;
    }
  }

  // --- stop polling ---
  abort() {
    this._aborted = true;
  }

  // --- get status once ---
  async getStatusNow() {
    if (!this.jobId) throw new Error("Job not started.");
    const r = await fetch(this._opts.statusPath(this.jobId), { cache: "no-store" });
    const j = await r.json();
    this._status = j;
    if (j.ready) this._ready = true;
    return j;
  }

  // --- download by click ---
  async download(filename = "icons.zip") {
    if (!this.jobId) throw new Error("Job not started.");
    if (!this._ready) throw new Error("File not ready yet.");

    const href = this._opts.downloadPath(this.jobId);
    const a = document.createElement("a");
    a.href = href;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
  }
}

export default Downloader;
